/**
 * Free Gift System
 *
 * Handles:
 * 1. Opening/closing the gift selection modal
 * 2. Adding selected gift to cart with _free_gift property
 * 3. Auto-removing gift when cart drops below threshold
 * 4. Subscribing to cart updates
 */

class FreeGiftManager {
  constructor() {
    this.modal = document.getElementById('FreeGiftModal');
    this.settings = window.freeGiftSettings || {};
    this.isProcessing = false;
    this.selectedVariantId = null;

    // If customer is blocked from free gifts, auto-remove any gift in cart
    if (this.settings.customerBlocked) {
      this.removeGiftIfPresent();
      return;
    }

    this.bindEvents();
    this.subscribeToCartUpdates();
  }

  async removeGiftIfPresent() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      for (const item of cartData.items) {
        if (item.properties && item.properties._free_gift === 'true') {
          await this.removeGiftFromCart(item.key);
          break;
        }
      }
    } catch (error) {
      console.error('Free Gift: Error removing gift for blocked customer', error);
    }
  }

  bindEvents() {
    // Open modal buttons (using event delegation)
    document.addEventListener('click', (e) => {
      const openButton = e.target.closest('[data-open-gift-modal]');
      if (openButton) {
        e.preventDefault();
        this.openModal();
        return;
      }

      const closeButton = e.target.closest('[data-close-gift-modal]');
      if (closeButton) {
        e.preventDefault();
        this.closeModal();
        return;
      }

      // Select a gift product (toggle selection)
      const selectButton = e.target.closest('[data-select-gift-variant]');
      if (selectButton && !this.isProcessing) {
        e.preventDefault();
        this.selectGift(selectButton);
        return;
      }

      // Add selected gift to cart
      const addButton = e.target.closest('[data-add-selected-gift]');
      if (addButton && !this.isProcessing && this.selectedVariantId) {
        e.preventDefault();
        this.addGiftToCart(this.selectedVariantId, addButton);
        return;
      }
    });

    // Escape key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('is-open')) {
        this.closeModal();
      }
    });
  }

  selectGift(buttonElement) {
    // Remove selection from all products
    this.modal.querySelectorAll('.free-gift-modal__product').forEach((el) => {
      el.classList.remove('is-selected');
    });

    // Select this product
    buttonElement.classList.add('is-selected');
    this.selectedVariantId = buttonElement.dataset.selectGiftVariant;

    // Enable add button
    const addButton = this.modal.querySelector('[data-add-selected-gift]');
    if (addButton) {
      addButton.disabled = false;
    }
  }

  subscribeToCartUpdates() {
    // Subscribe to cart update events if PubSub is available
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.checkGiftEligibility(event.cartData);
      });
    }

    // Also listen for custom cart events
    document.addEventListener('cart:updated', () => {
      this.fetchCartAndCheckGift();
    });
  }

  async fetchCartAndCheckGift() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        this.checkGiftEligibility(cartData);
      }
    } catch (error) {
      console.error('Free Gift: Error fetching cart', error);
    }
  }

  async checkGiftEligibility(cartData) {
    if (!cartData || !cartData.items) return;

    const progressEl = document.querySelector('.free-gift-progress');
    if (!progressEl) return;

    const threshold = parseInt(progressEl.dataset.giftThreshold) || this.settings.threshold;
    const giftLineKey = progressEl.dataset.giftLineKey;
    const hasGift = progressEl.dataset.hasGift === 'true';

    if (!hasGift || !giftLineKey) return;

    // Calculate cart total without gifts
    let cartTotalWithoutGifts = 0;

    for (const item of cartData.items) {
      // Skip gift items
      if (item.properties && item.properties._free_gift === 'true') {
        continue;
      }
      cartTotalWithoutGifts += item.final_line_price;
    }

    // If cart is below threshold AND has a gift, remove the gift
    if (cartTotalWithoutGifts < threshold && hasGift) {
      await this.removeGiftFromCart(giftLineKey);
    }
  }

  async addGiftToCart(variantId, buttonElement) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    // Visual feedback - show loading state on add button
    if (buttonElement) {
      buttonElement.setAttribute('disabled', 'disabled');
      buttonElement.innerHTML = '<span class="free-gift-modal__loading-spinner"></span>';
    }

    try {
      // First, check if there's already a gift in cart and remove it
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      let existingGiftKey = null;
      for (const item of cartData.items) {
        if (item.properties && item.properties._free_gift === 'true') {
          existingGiftKey = item.key;
          break;
        }
      }

      // Remove existing gift if present
      if (existingGiftKey) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: existingGiftKey,
            quantity: 0,
          }),
        });
      }

      // Add new gift with sections to render
      const sectionsToRender = this.getSectionsToRender();
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(variantId),
              quantity: 1,
              properties: {
                _free_gift: 'true',
              },
            },
          ],
          sections: sectionsToRender.map((s) => s.section),
          sections_url: window.location.pathname,
        }),
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add gift to cart');
      }

      const responseData = await addResponse.json();

      // Update cart sections with new HTML
      this.updateCartSections(responseData.sections, sectionsToRender);

      // Close modal
      this.closeModal();

      // Update cart count in header
      this.updateCartCount();

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // If using Dawn's PubSub, publish cart update
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
      }
    } catch (error) {
      console.error('Free Gift: Error adding gift to cart', error);
      // Reset button state
      if (buttonElement) {
        buttonElement.removeAttribute('disabled');
        buttonElement.textContent = this.getAddButtonText();
      }
    } finally {
      this.isProcessing = false;
    }
  }

  getSectionsToRender() {
    const sections = [];

    // Cart drawer
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      const sectionId = cartDrawer.dataset.id || 'cart-drawer';
      sections.push({
        section: sectionId,
        selector: '#CartDrawer',
      });
    }

    // Cart page items
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) {
      const sectionId = cartItems.dataset.id;
      if (sectionId) {
        sections.push({
          section: sectionId,
          selector: '#main-cart-items',
        });
      }
    }

    // Cart page footer
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) {
      const sectionId = cartFooter.dataset.id;
      if (sectionId) {
        sections.push({
          section: sectionId,
          selector: '#main-cart-footer',
        });
      }
    }

    return sections;
  }

  updateCartSections(sections, sectionsToRender) {
    if (!sections) return;

    sectionsToRender.forEach(({ section, selector }) => {
      const newHtml = sections[section];
      if (!newHtml) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(newHtml, 'text/html');
      const newContent = doc.querySelector(selector);
      const existingContent = document.querySelector(selector);

      if (newContent && existingContent) {
        existingContent.innerHTML = newContent.innerHTML;
      }
    });
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count bubbles
      const cartCounts = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
      cartCounts.forEach((el) => {
        el.textContent = cart.item_count;
      });

      // Show/hide cart count bubble based on count
      const cartBubbles = document.querySelectorAll('.cart-count-bubble');
      cartBubbles.forEach((el) => {
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });
    } catch (error) {
      console.error('Free Gift: Error updating cart count', error);
    }
  }

  getAddButtonText() {
    const existingButton = document.querySelector('[data-add-selected-gift]');
    return existingButton?.dataset.textAdd || 'Add gift';
  }

  async removeGiftFromCart(lineKey) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const sectionsToRender = this.getSectionsToRender();
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sectionsToRender.map((s) => s.section),
          sections_url: window.location.pathname,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();

        // Update cart sections with new HTML
        this.updateCartSections(responseData.sections, sectionsToRender);

        // Update cart count in header
        this.updateCartCount();

        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // If using Dawn's PubSub, publish cart update
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
        }
      }
    } catch (error) {
      console.error('Free Gift: Error removing gift from cart', error);
    } finally {
      this.isProcessing = false;
    }
  }

  openModal() {
    if (!this.modal) return;

    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('free-gift-modal-open');

    // Reset button state and set correct text
    const addButton = this.modal.querySelector('[data-add-selected-gift]');
    if (addButton) {
      addButton.disabled = true;
      const hasGift = document.querySelector('.free-gift-progress')?.dataset.hasGift === 'true';
      addButton.textContent = hasGift
        ? (addButton.dataset.textChange || 'Change gift')
        : (addButton.dataset.textAdd || 'Add gift');
    }

    // Focus first product button for accessibility
    const firstProduct = this.modal.querySelector('.free-gift-modal__product');
    if (firstProduct) {
      firstProduct.focus();
    }

    // Trap focus within modal
    this.trapFocus();
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('free-gift-modal-open');

    // Remove selection from all products
    this.modal.querySelectorAll('.free-gift-modal__product').forEach((el) => {
      el.classList.remove('is-selected');
    });

    // Reset selection state
    this.selectedVariantId = null;

    // Reset add button state and text
    const addButton = this.modal.querySelector('[data-add-selected-gift]');
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = addButton.dataset.textAdd || 'Add gift';
    }

    // Return focus to trigger button
    const triggerButton = document.querySelector('[data-open-gift-modal]');
    if (triggerButton) {
      triggerButton.focus();
    }
  }

  trapFocus() {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.modal.addEventListener('keydown', handleTabKey);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FreeGiftManager());
} else {
  new FreeGiftManager();
}
